import { css, Theme } from '@emotion/react';
import styled from 'components/styled';
import {
  borderRadius,
  BorderRadiusProps,
  display,
  DisplayProps,
  fontSize,
  FontSizeProps,
  fontWeight,
  FontWeightProps,
  lineHeight,
  LineHeightProps,
  minWidth,
  MinWidthProps,
  space,
  SpaceProps,
  width,
  WidthProps,
  compose,
} from 'styled-system';

interface AlertProps {
  size?: string;
  variant?: 'info' | 'warning' | 'error' | 'success';
  loading?: boolean;
}

type Props = AlertProps &
  SpaceProps &
  FontSizeProps &
  WidthProps &
  FontWeightProps &
  LineHeightProps &
  BorderRadiusProps &
  MinWidthProps &
  DisplayProps;

const loadingStyle = (theme: Theme) => css`
  position: relative;
  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    border: 4px solid ${theme.colors.darkGrey};
    border-left-color: white;
    border-radius: 50%;
    box-sizing: border-box;
    top: 50%;
    left: 50%;
    margin-top: -12px;
    margin-left: -12px;
    width: 24px;
    height: 24px;

    @keyframes ld {
      0% {
        transform: rotate(0deg) scale(1);
      }
      50% {
        transform: rotate(180deg) scale(1);
      }
      100% {
        transform: rotate(360deg) scale(1);
      }
    }

    animation: ld 1s linear infinite;
  }
`;

export const Alert = styled.div<Props>`
  ${({
    size = 'default',
    variant = 'info',
    loading = false,
    theme,
  }: AlertProps & { theme: Theme }) => {
    let backgroundColor;
    let fontColor;
    let border;
    let meta;

    const fontSize = theme.fontSizes[1];
    const px = theme.space[3];
    const py = theme.space[2];
    let justifyContent;

    switch (size) {
      case 'full':
        justifyContent = 'center';
        break;
    }

    switch (variant) {
      default:
      case 'info':
        backgroundColor = theme.colors.lightBlue;
        fontColor = theme.colors.information;
        border = `1px solid ${fontColor}`;
        break;
      case 'warning':
        backgroundColor = theme.colors.lightOrange;
        fontColor = theme.colors.warning;
        border = `1px solid ${fontColor}`;
        break;
      case 'error':
        backgroundColor = theme.colors.lightPink;
        fontColor = theme.colors.danger;
        border = `1px solid ${fontColor}`;
        break;
      case 'success':
        backgroundColor = theme.colors.lightLime;
        fontColor = theme.colors.darkLime;
        border = `1px solid ${theme.colors.lime}`;
        break;
    }

    return css`
      transition: all 0.5s ease;
      font-family: ${theme.fonts.body};
      background-color: ${backgroundColor};
      color: ${fontColor};
      font-size: ${fontSize};
      padding: ${py}px ${px}px;
      border: ${border};
      font-weight: 400;
      outline: none;
      display: flex;
      justify-content: ${justifyContent};
      ${loading ? loadingStyle(theme) : ''}
      ${meta}
    `;
  }}
  ${compose(
    space,
    fontSize,
    width,
    fontWeight,
    lineHeight,
    borderRadius,
    minWidth,
    display
  )}
`;

Alert.defaultProps = {
  size: 'medium',
  color: 'primary',
  borderRadius: 1,
};

export default Alert;
